import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type {
  ExperimentAuditEntry,
  ExperimentDocument,
  ExperimentState,
  ExperimentTransitionPayload,
} from '../experimentTypes';
import type { SegmentResponseMapData } from '../experimentTypes';
import type { FunnelABTestDecisionRow } from '../types';
import { checkPreRegistration } from '../experimentValidation';
import { ExperimentsApiClient } from '../api/experimentsClient';
import { CandidateReviewBlock } from './CandidateReviewBlock';
import { AdvancedReadinessPanel } from './AdvancedReadinessPanel';
import { ResearchReportDetailDialog } from './ResearchReportDetailDialog';
import { ResearchCycleLauncher } from './ResearchCycleLauncher';
import { WinnerRolloutDialog } from './WinnerRolloutDialog';
import { SegmentResponseMap } from './SegmentResponseMap';
import { StatTierChip } from './StatTierChip';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';
import { STAT_TIER_TEST_IDS } from '../testIds';
import { resolveDecisionVerdict, describeMetric } from '../decisionVerdict';
import {
  buildReportScopeLink,
  deriveSurfaceStatus,
  deriveTrustAssessment,
  parseGateSummary,
  selectDecisionRow,
  SURFACE_STATUS_LABEL,
  type ExperimentSurfaceStatus,
  type ProposalFeedRow,
  type ReviewSummaryBlock,
} from '../experimentSurface';
import {
  applyFeedIdentity,
  describeAnalysisRef,
  interpretAbDecision,
  interpretEmulationRun,
  type ExperimentInterpretation,
} from '../experimentInterpretation';

interface ExperimentCardProps {
  projectId: string;
  experiment: ExperimentDocument;
  /** A/B decision report rows for the hub window (AC-801); null while loading/unavailable. */
  decisions: FunnelABTestDecisionRow[] | null;
  onTransition: (id: string, payload: ExperimentTransitionPayload) => Promise<unknown>;
  onEdit: (experiment: ExperimentDocument) => void;
  /** FU part G (D-F4): a card-triggered report regeneration finished — reload the list. */
  onResearchGenerated?: () => void;
}

const STATE_COLOR: Record<ExperimentState, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  running: 'primary',
  ready_to_decide: 'warning',
  decided: 'success',
  stopped: 'info',
  archived: 'default',
};

const TYPE_LABEL: Record<ExperimentDocument['type'], string> = {
  web_ab: 'Web A/B',
  pricing: 'Pricing',
  discovery: 'Discovery',
  emulation: 'Emulation',
  analyst: 'Analyst',
  research: 'Research',
};

/** E2c Phase B: data-health chip colors on the research block (render-only). */
const RESEARCH_HEALTH_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  pass: 'success',
  warn: 'warning',
  fail: 'error',
};

/** Spec E E2b: analyst vitals strip — render-only, tiers come from the server. */
const ANALYST_VITAL_ROWS: Array<{ key: 'v1FirstStepConversion' | 'v2PaywallReach' | 'v3OverallPurchase' | 'v4PaywallConversion'; label: string }> = [
  { key: 'v1FirstStepConversion', label: 'V1 first step' },
  { key: 'v2PaywallReach', label: 'V2 paywall reach' },
  { key: 'v3OverallPurchase', label: 'V3 purchase' },
  { key: 'v4PaywallConversion', label: 'V4 paywall conv' },
];

const ANALYST_TIER_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  solid: 'success',
  directional: 'warning',
  insufficient: 'error',
};

const ANALYST_SEVERITY_COLOR: Record<string, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

/** D: verdict chip tones — mirror of the Paid Acquisition VerdictChip mapping. */
const ANALYST_VERDICT_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  kill: 'error',
  watch: 'warning',
  scale: 'success',
  too_young: 'default',
};

/** E3: humanized labels for W-tool names in the exposure-gate callout. */
const ANALYST_W_TOOL_LABEL: Record<string, string> = {
  candidate_compose: 'Candidate',
  hypothesis_draft: 'Draft',
};

/** W3 (J6): local tier-label map for lever-aware refusal copy (no cross-module dep). */
const AUTONOMY_TIER_LABEL: Record<string, string> = {
  L1: 'Observe',
  L2: 'Suggest',
  L3: 'Draft',
  L4: 'Apply with approval',
  L5: 'Autopilot',
};

const tierWithLabel = (tier: string): string =>
  AUTONOMY_TIER_LABEL[tier] ? `${tier} (${AUTONOMY_TIER_LABEL[tier]})` : tier;

const SURFACE_STATUS_COLOR: Record<ExperimentSurfaceStatus, 'default' | 'success' | 'warning' | 'error'> = {
  delivery_only: 'default',
  collecting: 'default',
  ready_to_decide: 'warning',
  decided: 'success',
  blocked_by_data_quality: 'error',
};

const VERDICT_COLOR: Record<'ship' | 'dont_ship' | 'keep_collecting', 'success' | 'error' | 'default'> = {
  ship: 'success',
  dont_ship: 'error',
  keep_collecting: 'default',
};

const TRUST_COLOR: Record<'ok' | 'warning' | 'critical', string> = {
  ok: 'text.secondary',
  warning: 'warning.main',
  critical: 'error.main',
};

const formatPercent = (value: number | null | undefined): string =>
  value == null ? '—' : `${Math.round(value * 100)}%`;

/** Chip colors for operator review decisions on feed rows (U7 AC-1008). */
const REVIEW_DECISION_CHIP: Record<string, { color: 'success' | 'default' | 'warning'; label: string }> = {
  accepted: { color: 'success', label: 'accepted' },
  rejected: { color: 'default', label: 'rejected' },
  deferred: { color: 'warning', label: 'deferred' },
};

/**
 * One proposal row in the read-only feed (AC-805). Review decisions render as
 * chips when `gateSummary.review` is persisted (U7) — still display-only; the
 * decisions themselves are recorded via `internal simulate review`.
 */
const ProposalFeedItem: React.FC<{
  row: ProposalFeedRow;
  testId: string;
  reviewDecision?: { decision: string; applyMode?: string };
  /** "in analysis <name> · <category>" caption for insight-backed rows (AC-1106). */
  analysisRef?: string | null;
}> = ({ row, testId, reviewDecision, analysisRef }) => {
  const arrow = row.direction === 'up' ? '↑' : row.direction === 'down' ? '↓' : '·';
  const chip = reviewDecision ? REVIEW_DECISION_CHIP[reviewDecision.decision] : undefined;
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap data-testid={testId}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {arrow} {row.targetName ?? row.targetId}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.family.replace(/_/g, ' ')} · {row.claim}
        {row.targetName ? ` · ${row.targetId}` : ''}
        {analysisRef ? ` · ${analysisRef}` : ''}
      </Typography>
      {row.actionable ? (
        <Chip size="small" color="success" variant="outlined" label="actionable" />
      ) : (
        <Typography variant="caption" color="warning.main">
          blocked{row.blockedReason ? `: ${row.blockedReason}` : ''}
          {row.nextEvidence ? ` — needs ${row.nextEvidence}` : ''}
        </Typography>
      )}
      {chip && (
        <Chip
          size="small"
          color={chip.color}
          variant="filled"
          label={reviewDecision?.applyMode ? `${chip.label} · ${reviewDecision.applyMode}` : chip.label}
        />
      )}
    </Stack>
  );
};

export const ExperimentCard: React.FC<ExperimentCardProps> = ({
  projectId,
  experiment,
  decisions,
  onTransition,
  onEdit,
  onResearchGenerated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [winnerDialogOpen, setWinnerDialogOpen] = useState(false);
  const [rolloutDialogOpen, setRolloutDialogOpen] = useState(false);
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  // FU part G (D-F4): card-scoped report regeneration (same window + series).
  const [researchRefreshing, setResearchRefreshing] = useState(false);
  const [researchRefreshError, setResearchRefreshError] = useState<string | null>(null);
  const [winnerVariantId, setWinnerVariantId] = useState('');
  const [winnerRationale, setWinnerRationale] = useState('');
  const [audit, setAudit] = useState<ExperimentAuditEntry[] | null>(null);
  const [segmentResponse, setSegmentResponse] = useState<SegmentResponseMapData | null>(null);
  const navigate = useNavigate();

  const client = useMemo(() => new ExperimentsApiClient(), []);
  const preRegistration = checkPreRegistration(experiment);

  // Iteration A: derived decision surface — display-only, registry untouched.
  const decisionRow = useMemo(() => selectDecisionRow(experiment, decisions), [experiment, decisions]);
  const verdict = useMemo(() => (decisionRow ? resolveDecisionVerdict(decisionRow) : null), [decisionRow]);
  const surfaceStatus = deriveSurfaceStatus(experiment, decisionRow);
  const trust = deriveTrustAssessment(experiment, decisionRow);
  const gateEvidence = useMemo(
    () => (experiment.type === 'emulation' ? parseGateSummary(experiment.gateSummary) : { kind: 'none' as const }),
    [experiment],
  );
  const reportScopeLink = buildReportScopeLink(projectId, experiment);
  const leadingVariantName = verdict?.leadingVariantId && decisionRow
    ? decisionRow.scores.find(score => score.variantId === verdict.leadingVariantId)?.variantName ?? verdict.leadingVariantId
    : null;

  // Iteration D: name-first feed rows (feedIdentity overlays Firestore names)
  // + brief → detail → action-items interpretation. Display-only.
  const feedRows = useMemo(
    () => (gateEvidence.kind === 'evidence' && gateEvidence.proposals
      ? applyFeedIdentity(gateEvidence.proposals.top, experiment.feedIdentity)
      : []),
    [gateEvidence, experiment.feedIdentity],
  );
  const interpretation = useMemo<ExperimentInterpretation | null>(() => {
    if (experiment.type === 'emulation') {
      if (gateEvidence.kind === 'none') return null;
      return interpretEmulationRun({
        surfaceStatus,
        trust,
        feed: feedRows,
        review: gateEvidence.kind === 'evidence' ? gateEvidence.review : undefined,
        decision: experiment.decision ?? null,
      });
    }
    if (experiment.type === 'web_ab' && verdict && decisionRow) {
      return interpretAbDecision({
        verdict,
        metricKey: decisionRow.primaryMetric,
        metricLabel: describeMetric(decisionRow.primaryMetric),
        testLabel: experiment.identity?.abTestName ?? decisionRow.testName ?? experiment.name,
        leadingVariantLabel: leadingVariantName,
        decided: Boolean(experiment.decision) || experiment.state === 'decided',
        placementId: experiment.abTestRef?.placementId ?? null,
      });
    }
    return null;
  }, [experiment, gateEvidence, surfaceStatus, trust, feedRows, verdict, decisionRow, leadingVariantName]);

  // Spec D4: the prepare button knows only the analysis — the same lineage ref
  // the interpretation deep links carry; analyst citations are the fallback.
  const candidateFromAnalysisId = useMemo<string | null>(() => {
    if (experiment.candidate?.basis.fromAnalysisId) return experiment.candidate.basis.fromAnalysisId;
    const fromAction = interpretation?.actions.find((action) => action.analysisId)?.analysisId;
    if (fromAction) return fromAction;
    const citation = experiment.analystReview?.findings
      .flatMap((finding) => finding.citations)
      .find((entry) => entry.kind === 'analysis');
    return citation?.ref ?? null;
  }, [experiment.candidate, interpretation, experiment.analystReview]);

  useEffect(() => {
    if (!expanded) return;
    const controller = new AbortController();
    client.getAuditLog(projectId, experiment.id, controller.signal)
      .then(setAudit)
      .catch(() => setAudit(null));
    if (experiment.type === 'discovery' && experiment.state !== 'draft') {
      client.getSegmentResponse(projectId, experiment.id, controller.signal)
        .then(setSegmentResponse)
        .catch(() => setSegmentResponse(null));
    }
    return () => controller.abort();
  }, [expanded, client, projectId, experiment.id, experiment.type, experiment.state, experiment.updatedAt]);

  const act = async (payload: ExperimentTransitionPayload) => {
    setActionError(null);
    try {
      await onTransition(experiment.id, payload);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Transition failed');
    }
  };

  const declareWinner = async () => {
    setActionError(null);
    try {
      await onTransition(experiment.id, {
        action: 'declare_winner',
        winnerVariantId: winnerVariantId.trim(),
        rationale: winnerRationale.trim(),
      });
      setWinnerDialogOpen(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Declare winner failed');
    }
  };

  const { state } = experiment;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={EXPERIMENTS_TEST_IDS.card(experiment.id)}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={TYPE_LABEL[experiment.type]}
          data-testid={EXPERIMENTS_TEST_IDS.cardType(experiment.id)}
        />
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 160 }}>{experiment.name}</Typography>
        <Chip
          size="small"
          label={state.replace(/_/g, ' ')}
          color={STATE_COLOR[state]}
          data-testid={EXPERIMENTS_TEST_IDS.cardState(experiment.id)}
        />
        <Chip
          size="small"
          variant="outlined"
          label={SURFACE_STATUS_LABEL[surfaceStatus]}
          color={SURFACE_STATUS_COLOR[surfaceStatus]}
          data-testid={EXPERIMENTS_TEST_IDS.cardSurfaceStatus(experiment.id)}
        />
        {/* R3-2: SRM lives in the header next to the state chips — the broken
            split is visible before any detail is expanded. */}
        {verdict?.sampleRatioMismatch && (
          <Chip
            size="small"
            color="error"
            label="SRM — split broken"
            data-testid={EXPERIMENTS_TEST_IDS.cardSrmBadge(experiment.id)}
          />
        )}
        {experiment.type === 'emulation' && experiment.status && (
          <Chip
            size="small"
            variant="outlined"
            label={`run: ${experiment.status.replace(/_/g, ' ')}`}
            color={experiment.status === 'completed' ? 'success' : experiment.status === 'error' ? 'error' : 'default'}
            data-testid={EXPERIMENTS_TEST_IDS.cardRunStatus(experiment.id)}
          />
        )}
        {experiment.type === 'emulation' && experiment.runTag && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={EXPERIMENTS_TEST_IDS.cardRunTag(experiment.id)}
          >
            {experiment.runTag}
          </Typography>
        )}

        {/* Research cards are handler-born observations: nothing to edit, nothing
            to run — the card's one action is "Open report" (invariant 7). */}
        {state === 'draft' && experiment.type !== 'research' && (
          <>
            <Button size="small" data-testid={EXPERIMENTS_TEST_IDS.actionEdit(experiment.id)} onClick={() => onEdit(experiment)}>
              Edit
            </Button>
            <Tooltip
              title={preRegistration.complete
                ? 'Start the experiment'
                : `Missing pre-registration: ${preRegistration.missing.join(', ')}`}
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!preRegistration.complete}
                  data-testid={EXPERIMENTS_TEST_IDS.actionRun(experiment.id)}
                  onClick={() => act({ action: 'run' })}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {(state === 'running' || state === 'ready_to_decide') && (
          <Button
            size="small"
            data-testid={EXPERIMENTS_TEST_IDS.actionStop(experiment.id)}
            onClick={() => act({ action: 'stop' })}
          >
            Stop
          </Button>
        )}
        {(state === 'running' || state === 'ready_to_decide') && surfaceStatus !== 'delivery_only' && surfaceStatus !== 'blocked_by_data_quality' && (
          <Button
            size="small"
            variant="contained"
            color="success"
            data-testid={EXPERIMENTS_TEST_IDS.actionDeclareWinner(experiment.id)}
            onClick={() => setWinnerDialogOpen(true)}
          >
            Declare winner
          </Button>
        )}
        {(state === 'decided' || state === 'stopped') && (
          <Button
            size="small"
            data-testid={EXPERIMENTS_TEST_IDS.actionArchive(experiment.id)}
            onClick={() => act({ action: 'archive' })}
          >
            Archive
          </Button>
        )}
        <Button
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(current => !current)}
        >
          Details
        </Button>
      </Stack>

      {state === 'draft' && !preRegistration.complete && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mt: 0.5 }}
          data-testid={EXPERIMENTS_TEST_IDS.runBlockedHint(experiment.id)}
        >
          Cannot run yet — missing: {preRegistration.missing.join(', ')}
        </Typography>
      )}
      {/* Trust rides the DATA, not the lifecycle: draft cards show it too once
          run evidence exists (e.g. a registered emulation run). */}
      {(state !== 'draft' || gateEvidence.kind !== 'none') && (
        <Box sx={{ mt: 0.5 }}>
          {/* D3: trust line upgraded to a chip — same testid + same text (the
              hub spec asserts both); evidence-origin glyph prefixed
              (⚪ simulated evidence vs 🟢 real traffic). */}
          <Chip
            size="small"
            variant="outlined"
            sx={{
              color: TRUST_COLOR[trust.severity],
              borderColor: TRUST_COLOR[trust.severity],
              height: 'auto',
              maxWidth: '100%',
              '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
            }}
            data-testid={EXPERIMENTS_TEST_IDS.trustLine(experiment.id)}
            label={`${experiment.type === 'emulation' ? '⚪' : '🟢'} ${trust.severity === 'ok' ? '✓' : '⚠'} ${trust.text}${trust.fix ? ` Fix: ${trust.fix}` : ''}`}
          />
        </Box>
      )}
      {/* Iteration D (AC-1102): the ONE-sentence brief — top action or state. */}
      {interpretation && (
        <Typography
          variant="body2"
          sx={{ mt: 0.5, fontWeight: 500 }}
          data-testid={EXPERIMENTS_TEST_IDS.interpretationHeadline(experiment.id)}
        >
          {interpretation.headline}
        </Typography>
      )}
      {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}

      <AdvancedReadinessPanel projectId={projectId} experimentId={experiment.id} />

      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={1.5}>
          {/* Spec E E2b: analyst review block — headline, vitals strip, findings
              with dedup/feedback badges, top actions. Render-only. */}
          {experiment.analystReview && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.analystBlock(experiment.id)}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                data-testid={EXPERIMENTS_TEST_IDS.analystHeadline(experiment.id)}
              >
                {experiment.analystReview.headline}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {experiment.analystReview.mode ?? 'release'} · reviewed {experiment.analystReview.reviewedAt.slice(0, 10)}
                {' · '}{experiment.analystReview.model}
                {experiment.analystReview.truncated ? ' · ⚠ truncated (partial report)' : ''}
              </Typography>
              {experiment.analystReview.decisionContext && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Decision brief for: {experiment.analystReview.decisionContext.experimentName}
                </Typography>
              )}
              {/* D: campaign-brief block — verdict chips quoted from the
                  deterministic rows + deep link into Paid Acquisition. */}
              {experiment.analystReview.campaignContext && (() => {
                const campaign = experiment.analystReview.campaignContext;
                const verdictRows = campaign.economics.filter((row) => row.verdict !== 'none');
                return (
                  <Box data-testid={EXPERIMENTS_TEST_IDS.analystCampaign(experiment.id)} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Campaign verdicts · {campaign.window.from} → {campaign.window.to}
                      {' · thresholds '}kill P90&lt;{campaign.thresholds.killBelowP90Roas}
                      {' / scale P10>'}{campaign.thresholds.scaleAboveP10Roas}
                      {' / min age '}{campaign.thresholds.minCohortAgeDays}d
                      {(() => {
                        // AC-H-8: the unmatched total renders only under a
                        // single known currency; mixed/missing states name
                        // WHY instead of showing a partial «$» figure.
                        if (campaign.unmatchedSpendMoneyState === 'mixed_currency') {
                          return ' · unmatched spend n/a (mixed ad-account currencies — sum withheld)';
                        }
                        if (campaign.unmatchedSpendMoneyState === 'missing_currency') {
                          return campaign.unmatchedSpend !== null && campaign.unmatchedSpend > 0
                            ? ` · unmatched spend ${Math.round(campaign.unmatchedSpend).toLocaleString()} (currency evidence missing; outside verdicts)`
                            : '';
                        }
                        if (campaign.unmatchedSpend !== null && campaign.unmatchedSpend > 0) {
                          const currency = campaign.moneyCurrency;
                          const amount = Math.round(campaign.unmatchedSpend).toLocaleString();
                          return ` · unmatched spend ${currency && currency !== 'USD' ? `${amount} ${currency}` : `$${amount}`} (outside verdicts)`;
                        }
                        return '';
                      })()}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {verdictRows.map((row) => (
                        <Chip
                          key={row.rowKey}
                          size="small"
                          variant="outlined"
                          color={ANALYST_VERDICT_COLOR[row.verdict] ?? 'default'}
                          data-testid={EXPERIMENTS_TEST_IDS.analystCampaignVerdict(
                            experiment.id,
                            row.metaCampaignId ?? row.campaign,
                          )}
                          label={`…${(row.metaCampaignId ?? row.campaign).slice(-6)}: ${
                            row.verdict === 'too_young' ? 'too young' : row.verdict
                          }${row.pltv12Roas !== null && row.pltv12Roas !== undefined ? ` · ${Number(row.pltv12Roas).toFixed(2)}×` : ''}`}
                        />
                      ))}
                    </Stack>
                    <MuiLink
                      component="button"
                      type="button"
                      variant="caption"
                      sx={{ mt: 0.5, display: 'inline-block' }}
                      data-testid={EXPERIMENTS_TEST_IDS.analystCampaignLink(experiment.id)}
                      onClick={() => navigate(`/project/${projectId}/reports?section=paid-acquisition`)}
                    >
                      Open Paid Acquisition
                    </MuiLink>
                  </Box>
                );
              })()}
              {(experiment.analystReview.actionsTaken?.length ?? 0) > 0 && (() => {
                const actions = experiment.analystReview?.actionsTaken ?? [];
                const candidateCount = actions.filter((entry) => entry.type === 'candidate_prepared').length;
                const draftCount = actions.filter((entry) => entry.type === 'draft_created').length;
                return (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 1 }}
                    flexWrap="wrap"
                    useFlexGap
                    data-testid={EXPERIMENTS_TEST_IDS.analystActionsTaken(experiment.id)}
                  >
                    {candidateCount > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        label={candidateCount === 1
                          ? 'Agent prepared a candidate'
                          : `Agent prepared ${candidateCount} candidates`}
                      />
                    )}
                    {draftCount > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        label={draftCount === 1
                          ? 'Agent drafted an experiment'
                          : `Agent drafted ${draftCount} experiments`}
                      />
                    )}
                  </Stack>
                );
              })()}
              {experiment.analystReview.vitals && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                  data-testid={EXPERIMENTS_TEST_IDS.analystVitals(experiment.id)}
                >
                  {ANALYST_VITAL_ROWS.map(({ key, label }) => {
                    const vital = experiment.analystReview?.vitals?.[key];
                    if (!vital) return null;
                    const tier = vital.qualification?.tier;
                    return (
                      <Stack key={key} spacing={0.25} alignItems="flex-start">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPercent(vital.value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        {tier && (
                          <Chip size="small" variant="outlined" label={tier} color={ANALYST_TIER_COLOR[tier] ?? 'warning'} />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {experiment.analystReview.findings.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Findings
                  </Typography>
                  {experiment.analystReview.findings.map((finding, index) => (
                    <Stack
                      key={finding.dedupKey || index}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      data-testid={EXPERIMENTS_TEST_IDS.analystFinding(experiment.id, index)}
                    >
                      <Chip size="small" label={finding.severity} color={ANALYST_SEVERITY_COLOR[finding.severity] ?? 'info'} variant="outlined" />
                      <Typography
                        variant="body2"
                        sx={finding.feedback?.verdict === 'dismissed' ? { textDecoration: 'line-through', color: 'text.disabled' } : undefined}
                      >
                        {finding.title}
                      </Typography>
                      {finding.duplicateOfPrior && <Chip size="small" label="repeat" variant="outlined" />}
                      {finding.feedback && (
                        <Chip
                          size="small"
                          label={finding.feedback.verdict}
                          color={finding.feedback.verdict === 'accepted' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  ))}
                </>
              )}
              {experiment.analystReview.topActions.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Top actions
                  </Typography>
                  {experiment.analystReview.topActions.map((action, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      data-testid={EXPERIMENTS_TEST_IDS.analystAction(experiment.id, index)}
                    >
                      {index + 1}. {action.action}
                      <Typography component="span" variant="caption" color="text.secondary"> — {action.expectedImpact}</Typography>
                    </Typography>
                  ))}
                </>
              )}
              {/* E3 exposure gate (W3 lever-aware): the agent attempted W actions
                  the autonomy tiers refuse. Render-only — attempts come from the
                  trajectory, never model text. Lever fields are additive; without
                  them the pre-Wave-3 copy renders byte-identically. */}
              {experiment.analystReview.wGate && (() => {
                const wGate = experiment.analystReview.wGate;
                const hasLeverContext = wGate.attempts.some((attempt) => attempt.lever || attempt.code);
                return (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ mt: 1.5 }}
                    data-testid={EXPERIMENTS_TEST_IDS.analystWGate(experiment.id)}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      The agent proposed {wGate.attempts.length}
                      {wGate.attempts.length === 1 ? ' action' : ' actions'}
                      {hasLeverContext
                        ? ' that the project’s autonomy tiers refuse'
                        : ' that require Proactive mode (off for this project)'}
                    </Typography>
                    {wGate.attempts.map((attempt, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        data-testid={EXPERIMENTS_TEST_IDS.analystWGateAttempt(experiment.id, index)}
                      >
                        {ANALYST_W_TOOL_LABEL[attempt.tool] ?? attempt.tool} — {attempt.summary}
                        {attempt.code === 'kill_switch'
                          ? ' — blocked by the project autonomy kill switch.'
                          : attempt.lever && attempt.currentTier && attempt.requiredTier
                            ? ` — ${attempt.lever} lever is at ${tierWithLabel(attempt.currentTier)} — ${tierWithLabel(attempt.requiredTier)} required.`
                            : ''}
                      </Typography>
                    ))}
                    <Button
                      size="small"
                      sx={{ mt: 0.5 }}
                      onClick={() => navigate(`/project/${projectId}/autonomy`)}
                      data-testid={EXPERIMENTS_TEST_IDS.analystWGateManage(experiment.id)}
                    >
                      Manage autonomy
                    </Button>
                  </Alert>
                );
              })()}
            </Box>
          )}
          {/* E2c Phase B: research report block — window/scope/health summary,
              anomaly hint from the card payload, lazy detail dialog. Render-only:
              every number below was computed server-side. */}
          {experiment.researchReport && (() => {
            const research = experiment.researchReport;
            const exitSummary = research.sectionSummaries.find(summary => summary.id === 'exit_screens');
            const anomalousScreens = Number(exitSummary?.headline?.anomalousScreens ?? 0);
            const changedSummary = research.sectionSummaries.find(summary => summary.id === 'what_changed');
            return (
              <Box data-testid={EXPERIMENTS_TEST_IDS.researchBlock(experiment.id)}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">Window</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {research.reportMeta.window.from.slice(0, 10)} → {research.reportMeta.window.to.slice(0, 10)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">Journeys / users</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {research.reportMeta.scope.journeys.toLocaleString('en-US')} / {research.reportMeta.scope.users.toLocaleString('en-US')}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">Conversions</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {research.reportMeta.scope.conversions.toLocaleString('en-US')}
                      {research.reportMeta.scope.journeys > 0
                        ? ` · ${((research.reportMeta.scope.conversions / research.reportMeta.scope.journeys) * 100).toFixed(2)}%`
                        : ''}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">Data health</Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`health: ${research.reportMeta.dataHealth}`}
                      color={RESEARCH_HEALTH_COLOR[research.reportMeta.dataHealth] ?? 'warning'}
                      data-testid={EXPERIMENTS_TEST_IDS.researchHealth(experiment.id)}
                    />
                  </Stack>
                </Stack>
                {anomalousScreens > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="error"
                      label={`${anomalousScreens} screen${anomalousScreens > 1 ? 's' : ''} lose more than peers`}
                      data-testid={EXPERIMENTS_TEST_IDS.researchAnomalies(experiment.id)}
                    />
                  </Box>
                )}
                {changedSummary && research.reportMeta.previousReportId && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    What changed vs {research.reportMeta.previousReportId}: {String(changedSummary.headline?.movements ?? 0)} tested movements
                  </Typography>
                )}
                {/* E2c Phase C: latest panel run — data-gated, absent means no panel ran yet. */}
                {research.panel && (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                    sx={{ mt: 1 }}
                    data-testid={EXPERIMENTS_TEST_IDS.researchPanelLine(experiment.id)}
                  >
                    <Typography variant="caption" color="text.secondary">Expert panel:</Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={research.panel.status}
                      color={research.panel.status === 'ok' ? 'success' : 'warning'}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${research.panel.personaSummaries.filter(persona => persona.status === 'ok').length}/${research.panel.personaSummaries.length} personas`}
                    />
                    {research.panel.scientist.status === 'ok' ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        label={`supported ${research.panel.scientist.verdictCounts.supported ?? 0}`}
                      />
                    ) : (
                      <Chip size="small" variant="outlined" color="error" label="scientist failed" />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${research.panel.draftExperimentIds.length} draft${research.panel.draftExperimentIds.length === 1 ? '' : 's'}`}
                    />
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">
                  <Button
                    size="small"
                    variant="contained"
                    data-testid={EXPERIMENTS_TEST_IDS.researchOpenReport(experiment.id)}
                    disabled={researchRefreshing}
                    onClick={() => setResearchDialogOpen(true)}
                  >
                    Open report
                  </Button>
                  {/* FU part G (D-F4): regenerate THIS card's report — same window + series. */}
                  <Button
                    size="small"
                    data-testid={EXPERIMENTS_TEST_IDS.researchReportRefresh(experiment.id)}
                    disabled={researchRefreshing}
                    onClick={async () => {
                      setResearchRefreshing(true);
                      setResearchRefreshError(null);
                      try {
                        await client.generateResearchReport(projectId, {
                          from: research.reportMeta.window.from,
                          to: research.reportMeta.window.to,
                          seriesId: research.seriesId,
                        });
                        onResearchGenerated?.();
                      } catch (refreshError) {
                        setResearchRefreshError(refreshError instanceof Error ? refreshError.message : 'Research report failed');
                      } finally {
                        setResearchRefreshing(false);
                      }
                    }}
                  >
                    Refresh report
                  </Button>
                  {researchRefreshing && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid={EXPERIMENTS_TEST_IDS.researchGenerating(experiment.id)}
                    >
                      Generating…
                    </Typography>
                  )}
                  <ResearchCycleLauncher projectId={projectId} reportId={experiment.id} />
                  <Typography variant="caption" color="text.secondary">
                    series {research.seriesId}
                  </Typography>
                </Stack>
                {researchRefreshError && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {researchRefreshError}
                  </Typography>
                )}
              </Box>
            );
          })()}
          {/* Iteration D (AC-1102/1103/1106): detail + numbered action items,
              targets name-first with deep links into analyses/audiences. */}
          {interpretation && (interpretation.details.length > 0 || interpretation.actions.length > 0) && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.interpretationBlock(experiment.id)}>
              {interpretation.details.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block">What this means</Typography>
                  {interpretation.details.map((line, index) => (
                    <Typography key={index} variant="body2" color="text.secondary">{line}</Typography>
                  ))}
                </>
              )}
              {interpretation.actions.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Action items
                  </Typography>
                  {interpretation.actions.map((action, index) => (
                    <Box key={index} data-testid={EXPERIMENTS_TEST_IDS.actionItem(experiment.id, index)}>
                      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">{index + 1}. {action.text}</Typography>
                        {/* AC-C1: every action kind renders its affordance, not inert text. */}
                        {action.kind === 'declare_winner' && (state === 'running' || state === 'ready_to_decide') && (
                          <Button
                            size="small"
                            color="success"
                            data-testid={EXPERIMENTS_TEST_IDS.actionItemDeclareWinner(experiment.id, index)}
                            onClick={() => setWinnerDialogOpen(true)}
                          >
                            Declare winner
                          </Button>
                        )}
                        {/* J2-I1: one-click winner rollout dialog (same testid as the
                            former placements navigation — hub specs keep passing). */}
                        {action.kind === 'rollout' && (
                          <Button
                            size="small"
                            data-testid={EXPERIMENTS_TEST_IDS.actionItemRollout(experiment.id, index)}
                            onClick={() => setRolloutDialogOpen(true)}
                          >
                            Roll out winner
                          </Button>
                        )}
                        {action.kind === 'fork_version' && (
                          <Button
                            size="small"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            data-testid={EXPERIMENTS_TEST_IDS.actionItemFork(experiment.id, index)}
                            onClick={() => navigate(`/project/${projectId}?tab=strategies`)}
                          >
                            Open strategies
                          </Button>
                        )}
                        {action.analysisId && (
                          <Button
                            size="small"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            data-testid={EXPERIMENTS_TEST_IDS.actionItemLink(experiment.id, index)}
                            onClick={() => navigate(`/project/${projectId}?tab=insides&insightId=${action.analysisId}`)}
                          >
                            Open analysis
                          </Button>
                        )}
                        {(action.audienceId || action.kind === 'adjust_audience') && (
                          <Button
                            size="small"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            data-testid={EXPERIMENTS_TEST_IDS.actionItemAudiences(experiment.id, index)}
                            onClick={() => {
                              const params = new URLSearchParams({ tab: 'audiences' });
                              if (action.audienceId) params.set('audienceId', action.audienceId);
                              if (action.targetEntity === 'actor' && action.targetId) params.set('actorId', action.targetId);
                              navigate(`/project/${projectId}?${params.toString()}`);
                            }}
                          >
                            Open audiences
                          </Button>
                        )}
                      </Stack>
                      {action.where && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
                          {action.where}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* Spec D4 (§5.1): one-button candidate conveyor. The stop valve
              (SRM / fix_measurement) makes the whole block vanish (AC-D4-5). */}
          <CandidateReviewBlock
            projectId={projectId}
            experiment={experiment}
            client={client}
            stopValve={Boolean(interpretation?.stopValve) || Boolean(verdict?.stopValve)}
            fromAnalysisId={candidateFromAnalysisId}
          />

          {(decisionRow || (experiment.abTestRef && state !== 'draft')) && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.decisionSurface(experiment.id)}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary">Decision</Typography>
                {verdict ? (
                  <Chip
                    size="small"
                    label={verdict.label}
                    color={VERDICT_COLOR[verdict.verdict]}
                    data-testid={EXPERIMENTS_TEST_IDS.decisionVerdict(experiment.id)}
                  />
                ) : (
                  <Chip
                    size="small"
                    label="Collecting"
                    variant="outlined"
                    data-testid={EXPERIMENTS_TEST_IDS.decisionVerdict(experiment.id)}
                  />
                )}
                {reportScopeLink && (
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon fontSize="inherit" />}
                    data-testid={EXPERIMENTS_TEST_IDS.openReportScope(experiment.id)}
                    onClick={() => navigate(reportScopeLink)}
                  >
                    Open in Reports
                  </Button>
                )}
              </Stack>
              {verdict && decisionRow ? (
                <>
                  {/* Spec D D1 (AC-D1-5): SRM failure paints the whole card red and
                      pauses follow-up — the interpretation keeps only fix_measurement. */}
                  {verdict.stopValve && (
                    <Alert severity="error" sx={{ mt: 0.5 }} data-testid={STAT_TIER_TEST_IDS.srmBanner}>
                      Traffic split is broken — the numbers below are not comparable. Fix the
                      assignment/measurement first; follow-up actions are paused.
                    </Alert>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{verdict.reason}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {decisionRow.testName ?? decisionRow.testId}
                    {decisionRow.iterationName ? ` · ${decisionRow.iterationName}` : ''}
                    {' · '}{describeMetric(decisionRow.primaryMetric)}
                    {leadingVariantName ? ` · leading: ${leadingVariantName}` : ''}
                    {' · P(beat control): '}{formatPercent(verdict.probabilityToBeatControl)}
                    {verdict.expectedLossPerUser != null ? ` · expected loss/user: ${verdict.expectedLossPerUser.toFixed(2)}` : ''}
                    {decisionRow.minSampleTarget !== undefined && decisionRow.minSampleProgress !== undefined
                      ? ` · ${Math.round(decisionRow.minSampleProgress * 100)}% of min sample (${decisionRow.minSampleTarget}/variant)`
                      : ''}
                  </Typography>
                  {/* Spec D D1 (AC-D1-4): variant rows outside the scorecard carry the tier chip. */}
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {decisionRow.scores.map(score => (
                      <Stack key={score.variantId} direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ minWidth: 120 }}>
                          {score.variantName ?? score.variantId}
                          {score.variantId === decisionRow.controlVariantId ? ' (control)' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {score.assignedUsers} users
                        </Typography>
                        {score.qualification && <StatTierChip qualification={score.qualification} />}
                      </Stack>
                    ))}
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No decision evidence for this test in the last 30 days yet.
                </Typography>
              )}
            </Box>
          )}

          {/* Research cards are observations — no hypothesis to show (E2c Phase B). */}
          {experiment.type !== 'research' && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Hypothesis</Typography>
              <Typography variant="body2">{experiment.hypothesis?.statement || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Primary metric: {experiment.hypothesis?.primaryMetric || '—'}
                {experiment.hypothesis?.expectedEffect ? ` · expected: ${experiment.hypothesis.expectedEffect}` : ''}
                {experiment.hypothesis?.successCriteria ? ` · success: ${experiment.hypothesis.successCriteria}` : ''}
                {experiment.hypothesis?.guardrails?.length ? ` · guardrails: ${experiment.hypothesis.guardrails.join(', ')}` : ''}
              </Typography>
            </Box>
          )}

          {experiment.decision && (
            <Alert severity={experiment.decision.verdict === 'ship' ? 'success' : 'warning'}>
              Decision: {experiment.decision.verdict === 'ship' ? 'Ship' : "Don't ship"}
              {experiment.decision.winnerVariantId ? ` (winner: ${experiment.decision.winnerVariantId})` : ''}
              {' — '}{experiment.decision.rationale} · {experiment.decision.decidedBy} · {experiment.decision.decidedAt}
            </Alert>
          )}

          {experiment.abTestRef && (
            <Typography variant="caption" color="text.secondary">
              Linked A/B test: {experiment.identity?.abTestName ?? experiment.abTestRef.abTestId}
              {experiment.identity?.abTestName ? ` · ${experiment.abTestRef.abTestId}` : ''}
              {' '}(placement {experiment.identity?.placementName ?? (experiment.abTestRef.placementId || 'n/a')})
            </Typography>
          )}

          {experiment.type === 'emulation' && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.emulationDetails(experiment.id)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">Emulation run</Typography>
                {reportScopeLink && (
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon fontSize="inherit" />}
                    data-testid={EXPERIMENTS_TEST_IDS.openReportScope(experiment.id)}
                    onClick={() => navigate(reportScopeLink)}
                  >
                    Open in Reports
                  </Button>
                )}
              </Stack>
              <Typography variant="body2">
                {[
                  experiment.runTag && `runTag: ${experiment.runTag}`,
                  experiment.environment && `env: ${experiment.environment}`,
                  experiment.experimentLevel && `level: ${experiment.experimentLevel}`,
                  experiment.runType && `type: ${experiment.runType}`,
                ].filter(Boolean).join(' · ') || '—'}
              </Typography>
              {experiment.substrate && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Substrate: {experiment.identity?.placementName
                    ? `${experiment.identity.placementName} (${experiment.substrate.alias ?? 'n/a'})`
                    : experiment.substrate.alias ?? 'n/a'}
                  {experiment.identity?.onboardingName ? ` · ${experiment.identity.onboardingName}` : ''}
                  {experiment.substrate.funnelId ? ` · ${experiment.substrate.funnelId}` : ''}
                  {experiment.substrate.versionId ? ` / ${experiment.substrate.versionId}` : ''}
                </Typography>
              )}
              {experiment.measurementWindow?.start && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Window: {experiment.measurementWindow.start} → {experiment.measurementWindow.end ?? 'open'}
                </Typography>
              )}
              {experiment.parentRunId && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Parent run: {experiment.parentRunId}
                </Typography>
              )}
              {experiment.debugBundlePath && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Bundle: {experiment.debugBundlePath}
                </Typography>
              )}
              {gateEvidence.kind !== 'none' && (
                <Box sx={{ mt: 1 }} data-testid={EXPERIMENTS_TEST_IDS.evidenceBlock(experiment.id)}>
                  <Typography variant="caption" color="text.secondary" display="block">Run evidence</Typography>
                  {gateEvidence.kind === 'failure' && (
                    <Typography variant="body2" color="error.main">
                      Run failed: {gateEvidence.message}
                    </Typography>
                  )}
                  {gateEvidence.kind === 'legacy' && gateEvidence.entries.map(entry => (
                    <Typography key={entry.key} variant="body2" color="text.secondary">
                      {entry.key}: {entry.value}
                    </Typography>
                  ))}
                  {gateEvidence.kind === 'evidence' && (
                    <>
                      {gateEvidence.resolver && (
                        <Typography variant="body2">
                          Resolver: {gateEvidence.resolver.answers ?? '—'} answers
                          {gateEvidence.resolver.lineageCoverage != null
                            ? ` · version coverage ${formatPercent(gateEvidence.resolver.lineageCoverage)}`
                            : ''}
                          {gateEvidence.resolver.classCounts
                            ? ` · ${Object.entries(gateEvidence.resolver.classCounts).map(([key, count]) => `${key}: ${count}`).join(', ')}`
                            : ''}
                        </Typography>
                      )}
                      {gateEvidence.estimator && (
                        <Typography variant="body2">
                          Estimator: {gateEvidence.estimator.rows ?? '—'} estimates
                          {gateEvidence.estimator.blocked != null ? ` · ${gateEvidence.estimator.blocked} blocked` : ''}
                          {gateEvidence.estimator.gatesVersion ? ` · gates ${gateEvidence.estimator.gatesVersion}` : ''}
                        </Typography>
                      )}
                      {gateEvidence.calibration && (
                        <Typography variant="body2" color="text.secondary">
                          Calibration: top-1 {formatPercent(gateEvidence.calibration.top1Accuracy)}
                          {gateEvidence.calibration.brier != null ? ` · Brier ${gateEvidence.calibration.brier}` : ''}
                          {gateEvidence.calibration.ece != null ? ` · ECE ${gateEvidence.calibration.ece}` : ''}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
              {gateEvidence.kind === 'evidence' && gateEvidence.proposals && (() => {
                const review: ReviewSummaryBlock | undefined = gateEvidence.review;
                const decisionById = new Map(
                  (review?.byId ?? []).map((d) => [d.proposalId, { decision: d.decision, applyMode: d.applyMode }]),
                );
                return (
                  <Box sx={{ mt: 1 }} data-testid={EXPERIMENTS_TEST_IDS.proposalsFeed(experiment.id)}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Proposals ({gateEvidence.proposals.actionable ?? 0} actionable / {gateEvidence.proposals.blocked ?? 0} blocked
                      {gateEvidence.proposals.rows != null ? ` of ${gateEvidence.proposals.rows}` : ''})
                    </Typography>
                    {experiment.proposalSummary && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>{experiment.proposalSummary}</Typography>
                    )}
                    {review && (
                      <Typography variant="body2" data-testid={EXPERIMENTS_TEST_IDS.reviewSummary(experiment.id)}>
                        Review: {review.counts.accepted ?? 0} accepted / {review.counts.rejected ?? 0} rejected / {review.counts.deferred ?? 0} deferred
                        {review.unresolvedRisks != null ? ` · ${review.unresolvedRisks} unresolved risk(s)` : ''}
                        {review.patchHash ? ` · candidate ${review.patchHash.slice(0, 12)}` : ''}
                      </Typography>
                    )}
                    {experiment.decision && (
                      <Typography variant="body2" color="text.secondary">
                        Decision: {experiment.decision.verdict}
                        {experiment.decision.rationale ? ` — ${experiment.decision.rationale}` : ''}
                      </Typography>
                    )}
                    {feedRows.length ? (
                      <Stack spacing={0.25}>
                        {feedRows.map((row, index) => (
                          <ProposalFeedItem
                            key={`${row.family}-${row.targetId}-${index}`}
                            row={row}
                            testId={EXPERIMENTS_TEST_IDS.proposalItem(experiment.id, index)}
                            reviewDecision={decisionById.get(`${row.family}:${row.targetId}`)}
                            analysisRef={describeAnalysisRef(row, experiment.feedIdentity)}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Feed not persisted for this run — re-run the replay to record the top proposals here.
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {review
                        ? 'Display-only — decisions recorded via `internal simulate review`; apply runs through the normal product chain (see apply-audit in the run bundle).'
                        : 'Read-only feed — record decisions via `internal simulate review` (U7).'}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}

          {segmentResponse && (
            <SegmentResponseMap experimentId={experiment.id} data={segmentResponse} />
          )}

          <Box data-testid={EXPERIMENTS_TEST_IDS.auditLog(experiment.id)}>
            <Typography variant="caption" color="text.secondary" display="block">Audit log</Typography>
            {!audit?.length ? (
              <Typography variant="body2" color="text.secondary">No transitions yet.</Typography>
            ) : (
              audit.map((entry, index) => (
                <Typography
                  key={entry.id}
                  variant="body2"
                  data-testid={EXPERIMENTS_TEST_IDS.auditEntry(experiment.id, index)}
                >
                  {entry.at} · {entry.actorUid} · {entry.action}
                  {entry.fromState ? ` (${entry.fromState} → ${entry.toState})` : ''}
                  {entry.details ? ` — ${entry.details}` : ''}
                </Typography>
              ))
            )}
          </Box>
        </Stack>
      </Collapse>

      {experiment.researchReport && (
        <ResearchReportDetailDialog
          open={researchDialogOpen}
          onClose={() => setResearchDialogOpen(false)}
          projectId={projectId}
          reportId={experiment.id}
          panel={experiment.researchReport.panel}
        />
      )}

      {/* J2-I1: mounted on demand so the preview call fires only when opened. */}
      {rolloutDialogOpen && (
        <WinnerRolloutDialog
          open={rolloutDialogOpen}
          onClose={() => setRolloutDialogOpen(false)}
          projectId={projectId}
          experimentId={experiment.id}
        />
      )}

      <SDialog
        open={winnerDialogOpen}
        onClose={() => setWinnerDialogOpen(false)}
        data-testid={EXPERIMENTS_TEST_IDS.winnerDialog}
        title="Declare winner"
        primaryAction={{
          label: 'Declare',
          onClick: declareWinner,
          disabled: !winnerVariantId.trim() || !winnerRationale.trim(),
          'data-testid': EXPERIMENTS_TEST_IDS.winnerConfirm,
        }}
      >
        <Stack spacing={1.5} sx={{ mt: 0.5, minWidth: 320, px: 3 }}>
          <TextField
            label="Winning variant id"
            size="small"
            value={winnerVariantId}
            onChange={(event) => setWinnerVariantId(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.winnerVariantInput }}
          />
          <TextField
            label="Rationale"
            size="small"
            multiline
            minRows={2}
            value={winnerRationale}
            onChange={(event) => setWinnerRationale(event.target.value)}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.winnerRationaleInput }}
          />
        </Stack>
      </SDialog>
    </Paper>
  );
};
